
<script setup lang="ts">
//#region imports
import { format, intervalToDuration } from 'date-fns';
import { computed, onMounted, ref, watch } from 'vue';
import { debounce as debounceFn, find } from 'lodash-es';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useFilters } from '@/setup/filters';
import { usePermissions } from '@/composables/usePermissions';
import { getDurationInUnit } from '@/helpers/date-helper';
import { getErrorMessage } from '@/types/core';
import type { ErrorList, ErrorMessage } from '@/types/core';
import type { CrmSegmentRuleFilter } from '@/types/crm';
import type { SegmentFormErrorsTypeDynamicFixed } from './popups/CreateSegment.vue';
//#endregion

//#region types
type RuleFilter = Omit<CrmSegmentRuleFilter, 'values'> & {
  values: any;
};

interface AvailableFilterItem {
  name: string;
  comparable?: boolean;
  type: string;
  dictionary?: string;
}

interface AvailableFilterGroup {
  name: string;
  items: AvailableFilterItem[];
}

interface Props {
  mode?: 'create' | 'view';
  groupRule?: RuleFilter;
  availableFilters?: AvailableFilterGroup[];
  errors?: SegmentFormErrorsTypeDynamicFixed | ErrorList;
  currentBrand: string;
}

interface Emits {
  deleteFilter: [];
  loading: [boolean];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  mode: 'create',
  groupRule: () => ({
    rule_type: '',
    operator: '',
    values: '',
  }),
  availableFilters: () => [],
  errors: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useStore();
const { t } = useI18n();
const api = useApi();
const filters = useFilters();
const { hasPermission } = usePermissions();
//#endregion

//#region state
const booleanList = [true, false];
const favouriteBetList = ['sport', 'games'];
const contactList = ['email', 'phone'];
const timeRanges = ['seconds', 'minutes', 'hours', 'days'] as const;
type TimeUnit = (typeof timeRanges)[number];
const time = ref<TimeUnit>('days');
const autocompleteQuery = ref('');
const autocompleteQueryResult = ref<any[] | null>(null);
const autocompleteQueryResultDefault = ref<any[]>([]);
const autocompleteDictionaryIndex = ref<Record<string, any>>({});
const dictionariesMap: Record<string, string> = {
  countries: 'MISC_FETCH_COUNTRIES',
  regions: 'MISC_FETCH_REGIONS',
  paymentsSystems: 'MISC_FETCH_PAYMENTS_SYSTEMS',
  sportType: 'MISC_FETCH_SPORT_TYPE',
  sportLeague: 'MISC_FETCH_SPORT_LEAGUE',
};
const date = ref('');
const dateRange = ref<any[]>([]);
//#endregion

//#region computed
const pFavouriteBetList = computed(() => favouriteBetList.map((el) => ({ id: el, name: t(`crm.segments.favourite_bet.${el}`) })));
const pTimeRanges = computed(() => timeRanges.map((el) => ({ id: el, name: t(`crm.segments.time_ranges.${el}`) })));
const pContactList = computed(() => contactList.map((el) => ({ id: el, name: t(`crm.segments.player_contacts.${el}`) })));
const isCreated = computed(() => props.mode === 'create');

const currentFilter = computed(() => {
  const filters = props.availableFilters.map(({ items }) => items).flat();
  return filters.find(({ name }) => name === props.groupRule.rule_type);
});

const hasDictionary = computed(() => currentFilter.value?.dictionary !== undefined);

const dictionaryForFilter = computed(() => {
  if (currentFilter.value) {
    const dictionary = store.getters[`misc/${currentFilter.value.dictionary}`] || [];
    const selectedValues = Array.isArray(props.groupRule.values) ? props.groupRule.values : [];
    const { head, tail } = dictionary.reduce((acc: { head: any[]; tail: any[] }, item: any) => {
      if (selectedValues.includes(item.code)) {
        acc.head.push(item);
      } else {
        acc.tail.push(item);
      }

      return acc;
    }, { head: [], tail: [] });

    return [...head, ...tail];
  }

  return [];
});

const timeValue = computed({
  get() {
    return getDurationInUnit(props.groupRule.values, time.value, 'custom');
  },
  set(value: string) {
    let valueTemp = value;
    if (value === '') valueTemp = '0';
    props.groupRule.values = getDurationInUnit(parseFloat(valueTemp), time.value, 'seconds');
  },
});

const autocompleteQueryResultDefaultComputed = computed(() => {
  const values = props.groupRule.values || [];
  const keys = autocompleteQueryResultDefault.value.map((item) => item.id);
  const valuesFiltered = values.filter((item: any) => !keys.includes(item.id));

  return valuesFiltered.concat(autocompleteQueryResultDefault.value);
});

const autocompleteQueryResultComputed = computed(() => {
  const source = autocompleteQueryResult.value || autocompleteQueryResultDefaultComputed.value;
  const { head, tail } = source.reduce((acc: { head: any[]; tail: any[] }, item: any) => {
    if ((props.groupRule.values || []).some(({ id }: any) => id === item.id)) {
      acc.head.push(item);
    } else {
      acc.tail.push(item);
    }

    return acc;
  }, { head: [], tail: [] });

  return [...head, ...tail];
});

const inputType = computed(() => getInputType(props.groupRule.rule_type));

const autocompleteValues = computed({
  get() {
    return props.groupRule.values
      ? props.groupRule.values.map((item: any) => (item?.id ? item.id : item))
      : [];
  },
  set(value: string[]) {
    props.groupRule.values = value.map((item) => ({
      id: item,
      email: autocompleteDictionaryIndex.value[item]?.email,
    }));
  },
});
//#endregion

//#region methods
function setAutocompleteQuery(value: string) {
  autocompleteQuery.value = value;
}

const setAutocompleteQueryDebouncedRaw = debounceFn(setAutocompleteQuery, 300);
function setAutocompleteQueryDebounced(value = '') {
  setAutocompleteQueryDebouncedRaw(value);
}

function formatDate(value: Date) {
  return format(value, 'yyyy-MM-dd HH:mm:ss');
}

function changeDateRange(value: Date | Date[] | null) {
  if (!value) {
    props.groupRule.values = [];
    return;
  }
  if (props.groupRule.operator === 'between') {
    const [d1, d2] = value as Date[];
    if (!d1 || !d2) {
      props.groupRule.values = [];
      return;
    }
    props.groupRule.values = [formatDate(d1), formatDate(d2)];
  } else {
    props.groupRule.values = formatDate(value as Date);
  }
}

function handleAffiliateSearch(search?: string) {
  if (!hasPermission('affiliates_view')) {
    return Promise.resolve([]);
  }

  const query = {
    search,
    limit: 50,
    referral_program_enabled: inputType.value === 'player_sub_affiliate_id' ? true : undefined,
    account_status: 'approved' as const,
  };

  return new Promise<any[]>((resolve, reject) => {
    api.getAffiliates(query)
      .then((response: any) => {
        updateAutocompleteDictionaryIndex(response.data.payload);
        resolve(response.data.payload);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

function handleAutocompleteVisibleChange(value: boolean) {
  setAutocompleteQuery('');
  setValueSelectPopupWidth(value);
}

function setValueSelectPopupWidth(_value: boolean) {}

function updateAutocompleteDictionaryIndex(list: any[]) {
  list.forEach((item) => {
    if (!autocompleteDictionaryIndex.value[item.id]) {
      autocompleteDictionaryIndex.value[item.id] = item;
    }
  });
}

function isInvalidField(fieldName: string) {
  return props.errors !== null ? Object.prototype.hasOwnProperty.call(props.errors, fieldName) : false;
}

function getError(fieldName: keyof SegmentFormErrorsTypeDynamicFixed) {
  return isInvalidField(fieldName) 
    ? getErrorMessage((props.errors as SegmentFormErrorsTypeDynamicFixed)?.[fieldName] as ErrorMessage) 
    : '';
}

function filteredOperators(ruleType: string) {
  if (ruleType) {
    if (currentFilter.value?.comparable) {
      const operators = ['>=', '<='];

      if (currentFilter.value.type === 'date_picker') {
        operators.push('between');
      }

      return operators;
    }
    return ['='];
  }
  return [];
}

function getDictionary() {
  if (currentFilter.value && hasDictionary.value) {
    const key = `misc/${currentFilter.value.dictionary}`;
    const dictionary = store.getters[key];
    if (dictionary === null || dictionary.length === 0) {
      store.dispatch(`misc/${dictionariesMap[currentFilter.value.dictionary as string]}`, props.currentBrand);
    }
  }
}

function clearDate() {
  date.value = '';
  dateRange.value = [];
}

function onChangeOperator() {
  if (getInputType(props.groupRule.rule_type) === 'date_picker') {
    clearDate();
  }
}

function changeRuleType() {
  getDictionary();
  props.groupRule.operator = filteredOperators(props.groupRule.rule_type).length === 1 ? '=' : '';
  props.groupRule.values = '';

  if (getInputType(props.groupRule.rule_type) === 'date_picker') {
    clearDate();
  }
}

function getInputType(ruleType: string) {
  const filters = props.availableFilters?.map(({ items }) => items).flat() || [];
  const filter = filters.find(({ name }) => name === props.groupRule.rule_type);

  return ruleType ? (filter?.type || 'text') : 'text';
}

function deleteFilter() {
  emit('deleteFilter');
}

function setPeriod() {
  if (props.groupRule.operator === 'between') {
    dateRange.value = props.groupRule.values;
  } else {
    date.value = props.groupRule.values;
  }
}

function setTimePeriod() {
  const duration = intervalToDuration({
    start: new Date(0),
    end: new Date((props.groupRule.values || 0) * 1000),
  });

  Object.keys(duration).forEach((key) => {
    if ((duration as Record<string, number>)[key] !== 0 && timeRanges.includes(key as TimeUnit)) {
      time.value = key as TimeUnit;
    }
  });

  if (props.groupRule.values === 0 || props.groupRule.values === '') {
    time.value = 'days';
  }
}

function getValues(type: string, value: any) {
  if (getInputType(type) === 'boolean') {
    return `${t(`crm.segments.boolean.${value}`)}`;
  }
  if (getInputType(type) === 'number') {
    return value;
  }
  if (getInputType(type) === 'sport_casino') {
    return `${t(`crm.segments.favourite_bet.${value}`)}`;
  }
  if (getInputType(type) === 'player_contacts') {
    return `${t(`crm.segments.player_contacts.${value}`)}`;
  }
  if (getInputType(type) === 'time') {
    return `${timeValue.value} ${t(`crm.segments.time_ranges.${time.value}`)}`;
  }
  if (getInputType(type) === 'date_picker') {
    return Array.isArray(value)
      ? value.map((val) => filters.formatDate3(val)).join(' - ')
      : filters.formatDate3(value);
  }

  if (value) {
    if (inputType.value === 'player_affiliate_id' || inputType.value === 'player_sub_affiliate_id') {
      if (value.length > 0) {
        return value.map((item: any) => item.email).join(', ');
      }
      return '';
    }

    getDictionary();
    const result = value.map((item: string) => {
      const filterItem = find(dictionaryForFilter.value, { code: item });
      return filterItem ? filterItem.name : '';
    });
    return result.join(', ');
  }

  return '';
}
//#endregion

//#region watch
watch(() => props.groupRule, (rule) => {
  if (getInputType(rule.rule_type) === 'time') {
    setTimePeriod();
  }

  if (getInputType(rule.rule_type) === 'date_picker') {
    setPeriod();
  }
}, { immediate: true, deep: true });

watch(autocompleteQuery, async (value) => {
  if ((inputType.value === 'player_affiliate_id' || inputType.value === 'player_sub_affiliate_id') && isCreated.value) {
    if (!value) {
      autocompleteQueryResult.value = null;
    } else {
      autocompleteQueryResult.value = await handleAffiliateSearch(value);
    }
  }
});

watch(inputType, async (value) => {
  if (value === 'player_affiliate_id' || value === 'player_sub_affiliate_id') {
    const promises: Promise<void>[] = [];

    if (isCreated.value) {
      promises.push(new Promise((resolve) => {
        handleAffiliateSearch().then((result) => {
          autocompleteQueryResultDefault.value = result;
          resolve();
        });
      }));
    }

    if (Array.isArray(props.groupRule.values) && props.groupRule.values.length > 0) {
      updateAutocompleteDictionaryIndex(props.groupRule.values);
    }

    try {
      emit('loading', true);
      await Promise.all(promises);
    } finally {
      emit('loading', false);
    }
  }
}, { immediate: true });
//#endregion

//#region hooks
onMounted(() => {
  getDictionary();
});
//#endregion
</script>

<template>
  <div
    :class="[
      'filter-item',
      `filter-item_mode-${mode}`
    ]"
  >
    <template v-if="isCreated">
      <UiButton
        type="negative"
        icon="minus"
        is-icon
        @click="deleteFilter()"
      />
      <UiSelectElement
        v-model="groupRule!.rule_type"
        :placeholder="$t('crm.segments.filters.select_rule_placeholder')"
        :width="200"
        filterable
        :error="getError('rule_type')"
        @change="changeRuleType"
      >
        <template v-slot:options>
          <ElOptionGroup
            v-for="filter in availableFilters"
            :key="filter.name"
            :label="$t(`crm.segments.filters.${filter.name}`)"
          >
            <ElOption
              v-for="item in filter.items"
              :key="item.name"
              :label="$t(`crm.segments.filters.${item.name}`)"
              :value="item.name"
            />
          </ElOptionGroup>
        </template>
      </UiSelectElement>
      <UiSelectElement
        v-model="groupRule!.operator"
        :placeholder="$t('crm.segments.filters.select_operator_placeholder')"
        :disabled="groupRule!.rule_type === ''"
        :width="150"
        filterable
        :error="getError('operator')"
        @change="onChangeOperator"
      >
        <template v-slot:options>
          <ElOption
            v-for="operator in filteredOperators(groupRule!.rule_type)"
            :key="operator"
            :label="$t(`crm.segments.operators.${operator}`)"
            :value="operator"
          />
        </template>
      </UiSelectElement>
      <UiSelectElement
        v-if="getInputType(groupRule!.rule_type) === 'text'"
        v-model="groupRule!.values"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        :list="dictionaryForFilter"
        multiple
        collapse-tag-tooltip
        :max-collapse-tags="1"
        :disabled="groupRule!.operator === ''"
        filterable
        search-param="name"
        option-value-param="code"
        :error="getError('values')"
      />
      <UiSelectElement
        v-if="(getInputType(groupRule!.rule_type) === 'player_affiliate_id'
          || getInputType(groupRule!.rule_type) === 'player_sub_affiliate_id')"
        v-model="autocompleteValues"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        :list="autocompleteQueryResultComputed"
        multiple
        collapse-tag-tooltip
        :max-collapse-tags="1"
        :disabled="groupRule!.operator === ''"
        remote
        filterable
        search-param="email"
        option-name-param="email"
        option-label-param="email"
        :remote-method="setAutocompleteQueryDebounced"
        :error="getError('values')"
      />
      <UiSelectElement
        v-if="getInputType(groupRule!.rule_type) === 'boolean'"
        v-model="groupRule!.values"
        :disabled="groupRule!.operator === ''"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        filterable
        :error="getError('values')"
      >
        <template v-slot:options>
          <ElOption
            v-for="value in booleanList"
            :key="String(value)"
            :label="$t(`crm.segments.boolean.${value}`)"
            :value="value"
          />
        </template>
      </UiSelectElement>
      <UiSelectElement
        v-if="getInputType(groupRule!.rule_type) === 'sport_casino'"
        v-model="groupRule!.values"
        :disabled="groupRule!.operator === ''"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        :list="pFavouriteBetList"
        filterable
        :error="getError('values')"
      />
      <UiSelectElement
        v-if="getInputType(groupRule!.rule_type) === 'player_contacts'"
        v-model="groupRule!.values"
        :disabled="groupRule!.operator === ''"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        :list="pContactList"
        filterable
        :error="getError('values')"
      />
      <UiInput
        v-if="getInputType(groupRule!.rule_type) === 'number'"
        v-model="groupRule!.values"
        :placeholder="$t('crm.segments.create_segment_value_placeholder')"
        autosize
        type="text"
        :disabled="groupRule!.operator === ''"
        :error="getError('values')"
      />
      <template v-if="getInputType(groupRule!.rule_type) === 'date_picker'">
        <UiDatePicker
          v-show="groupRule!.operator === 'between'"
          v-model="dateRange"
          class="t:w-full"
          :disabled="groupRule!.operator === ''"
          :placeholder="$t(`tools.match_popup.placeholder_date_time`)"
          :always-clearable="false"
          external-format="dd MMM yyyy HH:mm"
          enable-time-picker
          teleport
          :error="getError('values')"
          @update:model-value="changeDateRange"
        />
        <UiDatePicker
          v-show="groupRule!.operator !== 'between'"
          v-model="date"
          class="t:w-full"
          :disabled="groupRule!.operator === ''"
          :placeholder="$t(`tools.match_popup.placeholder_date_time`)"
          :multi-calendars="false"
          :range="false"
          :always-clearable="false"
          external-format="dd MMM yyyy HH:mm"
          enable-time-picker
          teleport
          :error="getError('values')"
          @update:model-value="changeDateRange"
        />
      </template>
      <div
        v-if="getInputType(groupRule!.rule_type) === 'time'"
        class="t:flex t:flex-1 t:gap-8"
      >
        <UiInput
          v-model="timeValue"
          :placeholder="$t('crm.segments.create_segment_value_placeholder')"
          type="text"
          :error="getError('values')"
          :disabled="groupRule!.operator === ''"
        />
        <UiSelectElement
          v-model="time"
          filterable
          :disabled="groupRule!.operator === ''"
          :list="pTimeRanges"
        />
      </div>
    </template>
    <template v-else>
      <div class="t:font-medium">
        {{ $t(`crm.segments.filters.${groupRule!.rule_type}`) }}
      </div>
      {{ $t(`crm.segments.operators.${groupRule!.operator}`) }}
      <div class="filter-item__value">
        {{ getValues(groupRule!.rule_type, groupRule!.values) }}
      </div>
    </template>
  </div>
</template>

<style lang="scss">
  .filter-item{
    display: flex;
    gap: 8px;
    padding-top: 8px;

    &_mode-view {
      display: inline-flex;
      align-items: center;
      padding: 6px 16px;
      gap: 8px;
      border-radius: 5px;
      background: var(--background-body);
      font-size: 12px;
    }
    &_mode-view & {
      &__value{
        display: flex;
        padding: 4px 8px;
        justify-content: center;
        align-items: center;
        color: var(--text-body);
        font-weight: 500;
        border-radius: 5px;
        border: 1px solid var(--border-default);
      }
    }
  }
</style>
